if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const internalPurchaseButtons = Array.from(this.querySelectorAll('[data-gmox-purchase-button]'));
        const externalPurchaseButtons = this.form?.id
          ? Array.from(document.querySelectorAll(`[data-gmox-purchase-button][form="${this.form.id}"]`))
          : [];
        this.submitButtons = Array.from(new Set([...internalPurchaseButtons, ...externalPurchaseButtons]));
        if (!this.submitButtons.length) this.submitButtons = Array.from(this.querySelectorAll('[type="submit"]'));
        this.submitButton = this.querySelector('[data-gmox-primary-submit]') || this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('[data-gmox-button-text], span');

        if (document.querySelector('cart-drawer')) {
          this.submitButtons
            .filter((button) => button.dataset.gmoxBuyNow !== 'true')
            .forEach((button) => button.setAttribute('aria-haspopup', 'dialog'));
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const activeButton = evt.submitter || this.submitButton;
        if (!activeButton || activeButton.disabled || activeButton.getAttribute('aria-disabled') === 'true') return;

        if (this.dataset.gmoxRequireVariantSelection === 'true' && !this.variantIdInput.value) {
          this.handleErrorMessage(this.dataset.gmoxVariantMessage || 'Please select an option.');
          this.closest('product-info')
            ?.querySelector('variant-selects input:not([disabled]), variant-selects select:not([disabled])')
            ?.focus();
          return;
        }

        const isBuyNow = activeButton.dataset.gmoxBuyNow === 'true';

        this.handleErrorMessage();
        this.setSubmitButtonsBusy(true, activeButton);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart && !isBuyNow) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = activeButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              activeButton.setAttribute('aria-disabled', true);
              activeButton.querySelector('[data-gmox-button-text], span')?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart && !isBuyNow) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            const cartUpdatePromise = !this.error
              ? publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              })
              : Promise.resolve();

            if (isBuyNow) {
              return Promise.resolve(cartUpdatePromise)
                .catch(() => {})
                .then(() => {
                  const rootUrl = window.routes.root_url || '/';
                  window.location.assign(`${rootUrl.replace(/\/?$/, '/')}checkout`);
                });
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.setSubmitButtonsBusy(false, activeButton);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.error = false;
        this.submitButtons.forEach((button) => {
          const label = button.querySelector('[data-gmox-button-text], span');
          if (disable) {
            button.setAttribute('disabled', 'disabled');
            if (text && label) label.textContent = text;
            return;
          }

          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
          if (label) label.textContent = button.dataset.defaultLabel || window.variantStrings.addToCart;
        });
      }

      setSubmitButtonsBusy(isBusy, activeButton) {
        this.submitButtons.forEach((button) => {
          if (isBusy) {
            button.setAttribute('aria-disabled', 'true');
          } else if (!this.error && !button.disabled) {
            button.removeAttribute('aria-disabled');
          }
          button.classList.toggle('loading', isBusy && button === activeButton);
          button.querySelector('.loading__spinner')?.classList.toggle('hidden', !(isBusy && button === activeButton));
        });
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
